import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'

import { cropToSquareBlob, LOGO_OUTPUT_SIZE } from './cropImage.js'

/**
 * Client-side crop step. The owner picks a file, this modal opens with the
 * source image, they drag-to-pan + zoom inside a locked 1:1 frame, and
 * "Confirm" produces an `outputSize`×`outputSize` PNG Blob handed back to the
 * caller for upload. The crop overlay can be a square or a circle guide
 * (`cropShape`) — either way the OUTPUT is a square PNG; the circle is just a
 * guide for logos, which Google Wallet renders in a circular frame.
 *
 * The server still re-validates square + size + mime (defense-in-depth) — the
 * crop is purely a UX affordance so owners don't have to pre-square their
 * images in another tool. See SLICE-4a-2 (logo) and SLICE-4a-3 (stamp icon,
 * which reuses this with `outputSize={200}`).
 *
 * No new modal primitive: this is a self-contained owner-themed overlay, the
 * same shape as the admin `Modal` but scoped to the owner tree (admin's lives
 * under `.admin-root` styling).
 */
export function LogoCropModal({
  file,
  onConfirm,
  onCancel,
  isUploading,
  outputSize = LOGO_OUTPUT_SIZE,
  title = 'Crop your logo',
  description = 'Drag to reposition and use the slider to zoom. The circle area is what customers see on their pass.',
  cropShape = 'rect',
}: {
  /** Source image the owner picked. A new `file` resets crop/zoom state. */
  file: File
  /** Called with the cropped PNG Blob (`outputSize` square) when confirmed. */
  onConfirm: (blob: Blob) => void
  /** Called on backdrop click, Escape, or the Cancel button. */
  onCancel: () => void
  /** True while the upload mutation is in flight — disables the controls. */
  isUploading: boolean
  /** Output edge length in px. 600 for logos, 200 for stamp icons. */
  outputSize?: number
  /** Modal heading. */
  title?: string
  /** Helper line under the heading. */
  description?: string
  /**
   * Crop overlay shape. 'round' shows a circular guide — used for logos,
   * since Google Wallet renders the loyalty logo inside a circular frame, so
   * the owner should center the logo for that. The OUTPUT is still a square
   * PNG either way (react-easy-crop reports the same square `Area`); 'round'
   * only changes the overlay, not the saved bytes. Defaults to 'rect'.
   */
  cropShape?: 'rect' | 'round'
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Object URL for the source preview. Recreated whenever `file` changes (the
  // owner cancelled and picked a different image) so we never show file A's
  // pixels after they've chosen file B.
  useEffect(() => {
    const url = URL.createObjectURL(file)
    setImageUrl(url)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setError(null)
    return () => URL.revokeObjectURL(url)
  }, [file])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isUploading && !processing) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, isUploading, processing])

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const busy = isUploading || processing

  async function confirm() {
    if (!croppedAreaPixels) return
    setError(null)
    setProcessing(true)
    try {
      const blob = await cropToSquareBlob(file, croppedAreaPixels, outputSize)
      onConfirm(blob)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not process the image. Please try again.',
      )
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div
      className="owner-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onCancel()
      }}
    >
      <div
        className="owner-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="owner-modal-head">
          <h3>{title}</h3>
        </div>
        <p className="owner-card-sub">{description}</p>

        <div className="owner-crop-stage">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape={cropShape}
              showGrid={cropShape === 'rect'}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <label className="owner-crop-zoom">
          <span>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            disabled={busy}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom"
          />
        </label>

        {error && (
          <p className="owner-field-error" role="alert">
            {error}
          </p>
        )}

        <div className="owner-modal-actions">
          <button
            type="button"
            className="owner-btn"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={confirm}
            disabled={busy || !croppedAreaPixels}
          >
            {busy ? 'Uploading…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  )
}
